import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type TenantDetail } from '../lib/api';

/** Live status card for an in-flight domain swap. Mirrors CfDnsCard
 * but for `state_blob.pending_swap` — the zone Sendepot created for
 * the operator's new domain after they hit POST /domain-swap.
 *
 * Visible while the swap is awaiting NS delegation, briefly during
 * the swap job itself, and not at all once the swap worker clears
 * the state_blob entry on success.
 */
export default function PendingSwapCard({ tenant }: { tenant: TenantDetail }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ps = tenant.pending_swap;
  if (!ps) return null;

  const ns = ps.cf_nameservers || [];
  const status = ps.ns_status || 'pending';

  async function handleCheck() {
    setErr(null); setBusy(true);
    try {
      // /cf-zone/check is now inline — it queries CF directly and
      // updates state before responding, so we invalidate immediately
      // to refetch the fresh tenant payload.
      await api.checkCfZone(tenant.id);
      await qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    } catch (e: any) {
      setErr(e?.message || 'check failed');
    } finally { setBusy(false); }
  }

  if (status === 'timeout' || status === 'failed') {
    return (
      <div className="card p-5 border border-err/30">
        <div className="text-xs uppercase tracking-wider text-dim mb-1">
          Domain swap — {status === 'timeout' ? 'timed out' : 'zone error'}
        </div>
        <div className="text-sm text-err mb-3">
          {status === 'timeout'
            ? `Cloudflare didn't confirm delegation for ${ps.to_domain} within 3 days.`
            : `Cloudflare zone setup for ${ps.to_domain} failed.`}{' '}
          Update the nameservers at your registrar and re-check, or restart the swap from the Domain swap tab.
        </div>
        {ns.length > 0 && (
          <div className="space-y-2">
            {ns.map(h => (
              <div key={h} className="flex items-center justify-between gap-3 rounded-md bg-white/[0.03] px-3 py-2 font-mono text-sm">
                <span className="truncate">{h}</span>
                <button onClick={() => navigator.clipboard?.writeText(h)} className="text-xs text-dim hover:text-ink" title="Copy">Copy</button>
              </div>
            ))}
          </div>
        )}
        <button onClick={handleCheck} disabled={busy} className="btn-primary mt-3 disabled:opacity-50">
          {busy ? 'Checking…' : 'Re-check now'}
        </button>
        {err && <div className="mt-2 text-xs text-err">{err}</div>}
      </div>
    );
  }

  if (status === 'active' || status === 'running') {
    // Active = CF just confirmed; running = swap job has been enqueued
    // / is executing. Render the same banner for both since they're a
    // few seconds apart and an operator doesn't care about the
    // distinction. The card disappears when run_domain_swap clears
    // state_blob.pending_swap on success.
    return (
      <div className="card p-5 border border-accent/30">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-accent">
            Domain swap — running
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-accent">
            <span className="inline-block h-2 w-2 rounded-full bg-accent animate-pulse" />
            in progress
          </span>
        </div>
        <div className="text-sm text-ink mb-2">
          Cloudflare confirmed delegation for{' '}
          <span className="font-mono">{ps.to_domain}</span>. Sendepot is
          now verifying the domain with Microsoft, pushing mail records,
          renaming every mailbox, and reconnecting them to your sequencer
          under the new addresses.
        </div>
        <div className="text-[12px] text-dim">
          This typically takes a few minutes — the {ps.to_domain} card
          will disappear when the swap finishes, and the mailbox
          connections bar above will refill as inboxes reconnect.
        </div>
      </div>
    );
  }

  // pending
  return (
    <div className="card p-5 border border-warn/30">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-dim">Domain swap — pending</div>
        <span className="text-xs text-warn">Awaiting nameserver delegation</span>
      </div>
      <div className="text-sm text-ink mb-3">
        At your domain registrar (GoDaddy, Namecheap, etc.), set the nameservers below for{' '}
        <span className="font-mono">{ps.to_domain}</span>. The swap from{' '}
        <span className="font-mono">{ps.from_domain}</span> runs automatically once delegation is confirmed.
      </div>
      <div className="space-y-2">
        {ns.map(h => (
          <div key={h} className="flex items-center justify-between gap-3 rounded-md bg-white/[0.03] px-3 py-2 font-mono text-sm">
            <span className="truncate">{h}</span>
            <button onClick={() => navigator.clipboard?.writeText(h)} className="text-xs text-dim hover:text-ink" title="Copy">Copy</button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button onClick={handleCheck} disabled={busy} className="btn-primary disabled:opacity-50">
          {busy ? 'Checking…' : 'Check now'}
        </button>
        <span className="text-[11px] text-dim">
          {ps.attempts ? `${ps.attempts} check${ps.attempts === 1 ? '' : 's'} so far. ` : ''}
          Rate-limited to 1 check/minute.
        </span>
      </div>
      {err && <div className="mt-2 text-xs text-err">{err}</div>}
    </div>
  );
}
